// Export a single asset's record. Used from the Asset detail page.
//
// - XLSX / CSV: single-row tabular shape matching exportAssetList columns
// - JSON: nested object with property + custom fields + photos + documents
//
// Deep-link "Asset URL" column is included for spreadsheet workflows.

import { supabase } from "@/shared/integrations/supabase/client";
import { getAppOriginUrl } from "@/shared/lib/appBasePath";
import * as XLSX from "xlsx-js-style";
import JSZip from "jszip";
import { csvCell, sanitize, triggerDownload, triggerBlobDownload } from "./_helpers";
import { downloadWorkbook } from "./_xlsx";

export type SingleAssetExportFormat = "xlsx" | "csv" | "json" | "zip";

export interface ExportSingleAssetOptions {
  assetId: string;
  // Project id for building the in-app deep link. Defaults to query if omitted.
  projectId?: string;
  format: SingleAssetExportFormat;
}

interface AssetRow {
  id: string;
  property_id: string;
  name: string | null;
  description: string | null;
  category_id: string | null;
  asset_categories: { name: string } | null;
  manufacturer: string | null;
  model_name: string | null;
  model_number: string | null;
  serial_number: string | null;
  manufacture_date: string | null;
  estimated_age_years: number | null;
  condition_status: string | null;
  status: string | null;
  category_attributes: Record<string, unknown> | null;
  notes: string | null;
  ref_id: string | null;
  zone_id: string | null;
  project_zones: { name: string } | null;
  nameplate_photo_url: string | null;
  asset_photo_url: string | null;
  qr_code_id: string | null;
}

interface PropertyLite {
  id: string;
  name: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  project_id: string | null;
}

interface DocumentRow {
  id: string;
  document_name: string | null;
  document_type: string | null;
  category: string | null;
  file_path: string | null;
  mime_type: string | null;
  created_at: string | null;
}

export async function exportSingleAsset(opts: ExportSingleAssetOptions): Promise<void> {
  const { assetId, format } = opts;

  // ── Fetch asset
  const { data: asset, error: assetErr } = await supabase
    .from("property_assets")
    .select(
      "id, property_id, name, description, category_id, asset_categories(name), manufacturer, " +
        "model_name, model_number, serial_number, manufacture_date:manufacture_date_string, estimated_age_years, " +
        "condition_status, status, " +
        "category_attributes, notes, ref_id, zone_id, project_zones(name), nameplate_photo_url, asset_photo_url, qr_code_id",
    )
    .eq("id", assetId)
    .maybeSingle();
  if (assetErr) throw assetErr;
  if (!asset) throw new Error("Asset not found");
  const a = asset as unknown as AssetRow;

  // ── Fetch parent property
  const { data: property } = await supabase
    .from("properties")
    .select("id, name, address, city, state, project_id")
    .eq("id", a.property_id)
    .maybeSingle();
  const p = (property ?? null) as PropertyLite | null;

  const baseUrl = typeof window !== "undefined" ? getAppOriginUrl() : "";
  const assetUrl = `${baseUrl}/asset/${a.id}`;

  const safeLabel = (a.name || a.id).replace(/[^a-z0-9]/gi, "_").toLowerCase();

  const categoryName = a.asset_categories?.name || "Uncategorized";
  const attrs = a.category_attributes || {};
  const sizeTons = attrs.tonnage != null ? Number(attrs.tonnage) : null;
  const refrigerantType = attrs.refrigerant_type || null;

  if (format === "json" || format === "zip") {
    // Rich shape: include documents + photos
    const { data: documentsData } = await supabase
      .from("asset_documents")
      .select("id, document_name, document_type, category, file_path, mime_type, created_at")
      .is("deleted_at", null)
      .eq("asset_id", a.id);

    const docs = (documentsData ?? []) as DocumentRow[];
    const photos = docs.filter((d) => d.document_type === "photo");
    const otherDocs = docs.filter((d) => d.document_type !== "photo");

    const payload = {
      url: assetUrl,
      asset: {
        id: a.id,
        ref_id: a.ref_id,
        name: a.name,
        category: categoryName,
        manufacturer: a.manufacturer,
        model_name: a.model_name,
        model_number: a.model_number,
        serial_number: a.serial_number,
        condition_status: a.condition_status,
        status: a.status,
        manufacture_date: a.manufacture_date,
        estimated_age_years: a.estimated_age_years ?? null,
        size_tons: sizeTons,
        refrigerant_type: refrigerantType,
        notes: a.notes,
        zone: a.project_zones?.name || null,
        nameplate_photo_url: a.nameplate_photo_url,
        asset_photo_url: a.asset_photo_url,
        qr_code_id: a.qr_code_id,
      },
      property: p
        ? { id: p.id, name: p.name, address: p.address, city: p.city, state: p.state }
        : { id: a.property_id },
      photos,
      documents: otherDocs,
    };

    if (format === "json") {
      triggerDownload(
        JSON.stringify(payload, null, 2),
        `${safeLabel}.json`,
        "application/json",
      );
      return;
    }

    // format === "zip" — bundle asset.json + photos + other documents
    const zip = new JSZip();
    zip.file("asset.json", JSON.stringify(payload, null, 2));

    const failures: string[] = [];

    // Photos → photos/<category>/<original_name>
    const photosFolder = zip.folder("photos")!;
    for (const photo of photos) {
      if (!photo.file_path) continue;
      const bytes = await fetchBytes(photo.file_path);
      if (!bytes) {
        failures.push(`photo ${photo.id} (${photo.document_name ?? photo.file_path})`);
        continue;
      }
      const folderName = sanitize(photo.category || "general");
      const ext = guessExt(photo.document_name, photo.mime_type) || ".jpg";
      const name = sanitize(photo.document_name || photo.id);
      // If sanitize already preserved an extension, don't append again
      const finalName = /\.[a-z0-9]{1,5}$/i.test(name) ? name : `${name}${ext}`;
      photosFolder.folder(folderName)!.file(finalName, bytes);
    }

    // Other documents → documents/<category>/<original_name>
    if (otherDocs.length > 0) {
      const docsFolder = zip.folder("documents")!;
      for (const doc of otherDocs) {
        if (!doc.file_path) continue;
        const bytes = await fetchBytes(doc.file_path);
        if (!bytes) {
          failures.push(`document ${doc.id} (${doc.document_name ?? doc.file_path})`);
          continue;
        }
        const folderName = sanitize(doc.category || "general");
        const ext = guessExt(doc.document_name, doc.mime_type) || "";
        const name = sanitize(doc.document_name || doc.id);
        const finalName = ext && !/\.[a-z0-9]{1,5}$/i.test(name) ? `${name}${ext}` : name;
        docsFolder.folder(folderName)!.file(finalName, bytes);
      }
    }

    if (failures.length > 0) {
      zip.file(
        "SKIPPED.txt",
        [
          `${failures.length} file(s) could not be fetched (CORS, network, or 404):`,
          "",
          ...failures,
        ].join("\n"),
      );
    }

    const zipBlob = await zip.generateAsync({
      type: "blob",
      compression: "DEFLATE",
      compressionOptions: { level: 5 },
    });
    triggerBlobDownload(zipBlob, `${safeLabel}.zip`);
    return;
  }

  // ── XLSX / CSV — single-row tabular shape, same columns as exportAssetList
  const header: Array<string | number | null> = [
    "Store", "Address", "City", "State",
    "Category", "Name", "Description",
    "Manufacturer", "Model Name", "Model Number", "Serial Number",
    "Manufacture Date", "Estimated Age (Years)", "Condition Status",
    "Location in Store",
    "Attributes", "Notes", "QR Code ID", "Nameplate Photo", "Asset Photo",
  ];

  const formatDate = (d: string | null) => {
    if (!d) return "";
    try { return new Date(d).toLocaleDateString("en-US", { year: "numeric", month: "2-digit", day: "2-digit" }); }
    catch { return d; }
  };

  const flattenAttrs = (obj: Record<string, unknown> | null) => {
    if (!obj || typeof obj !== "object") return "";
    return Object.entries(obj)
      .filter(([, v]) => v != null && v !== "")
      .map(([k, v]) => `${k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}: ${String(v)}`)
      .join("; ");
  };

  const row: Array<string | number | null> = [
    p?.name ?? "", p?.address ?? "", p?.city ?? "", p?.state ?? "",
    categoryName, a.name ?? "", a.description ?? "",
    a.manufacturer ?? "", a.model_name ?? "", a.model_number ?? "", a.serial_number ?? "",
    formatDate(a.manufacture_date), a.estimated_age_years ?? "", a.condition_status ?? "",
    a.project_zones?.name ?? "",
    flattenAttrs(a.category_attributes), a.notes ?? "", a.qr_code_id ?? "", a.nameplate_photo_url ?? "", a.asset_photo_url ?? "",
  ];

  if (format === "csv") {
    const csv = [header, row].map((r) => r.map(csvCell).join(",")).join("\n");
    triggerDownload(csv, `${safeLabel}.csv`, "text/csv;charset=utf-8");
    return;
  }

  // XLSX
  const ws = XLSX.utils.aoa_to_sheet([header, row]);
  const range = XLSX.utils.decode_range(ws["!ref"] ?? "A1");
  for (let c = range.s.c; c <= range.e.c; c++) {
    const ref = XLSX.utils.encode_cell({ r: 0, c });
    if (!ws[ref]) continue;
    ws[ref].s = {
      font: { color: { rgb: "FFFFFF" }, bold: true },
      fill: { fgColor: { rgb: "000000" } },
      alignment: { wrapText: true, vertical: "top" },
    };
  }
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Asset");
  downloadWorkbook(wb, `${safeLabel}.xlsx`);
}

// Fetch raw bytes for either an absolute https URL (Firebase Storage) or a
// Supabase Storage path. We assume `file_path` is one of those two; both are
// what asset_documents.file_path contains in this codebase.
async function fetchBytes(filePath: string): Promise<Uint8Array | null> {
  try {
    if (filePath.startsWith("http://") || filePath.startsWith("https://")) {
      const resp = await fetch(filePath);
      if (!resp.ok) return null;
      return new Uint8Array(await resp.arrayBuffer());
    }
    // Supabase Storage path: assume the `asset-documents` bucket since that's
    // where assetDocuments.file_path resolves in the codebase pattern.
    const { data, error } = await supabase.storage
      .from("asset-documents")
      .download(filePath);
    if (error || !data) return null;
    return new Uint8Array(await data.arrayBuffer());
  } catch (e) {
    console.warn("fetchBytes failed for", filePath, e);
    return null;
  }
}

function guessExt(filename: string | null | undefined, mime: string | null | undefined): string | null {
  if (filename) {
    const m = filename.match(/\.([a-z0-9]{1,5})$/i);
    if (m) return `.${m[1].toLowerCase()}`;
  }
  if (mime) {
    if (mime.includes("jpeg") || mime.includes("jpg")) return ".jpg";
    if (mime.includes("png")) return ".png";
    if (mime.includes("webp")) return ".webp";
    if (mime.includes("pdf")) return ".pdf";
  }
  return null;
}
